import "./WalletsList.scss";
import { useEffect, useMemo, useState } from "react";
import { Search, Wallet as WalletIcon, XCircle } from "lucide-react";
import { PageCard, EmptyState } from "../../../components/ui";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchUserWallets, getUserWallets, setUserWallets } from "../../../store/UserSlice";
import { WalletInterface } from "../../../models/Wallet";
import { formatAssetWithSymbol } from "../../../utils/decimal";
import { groupWalletsByCurrency, sumWalletsBalance } from "../../../utils/wallet-totals";
import WalletCard from "../../../components/wallet-card/WalletCard";

const WalletsList = () => {
    const dispatch = useAppDispatch();
    const userInfo = useAppSelector((s) => s.user.userInfo);
    const wallets = useAppSelector(getUserWallets) as WalletInterface[] | null;

    const [search, setSearch] = useState("");
    const [moeda, setMoeda] = useState("");
    const [empresa, setEmpresa] = useState("");
    const [status, setStatus] = useState("");

    useEffect(() => {
        dispatch(fetchUserWallets(userInfo.id)); 
    }, [dispatch]);

    const moedasDisponiveis = useMemo(() => {
        if (!wallets) return [];
        return Array.from(new Set(wallets.map((w) => w.asset.symbol)));
    }, [wallets]);

    const empresasDisponiveis = useMemo(() => {
        if (!wallets) return [];
        return Array.from(
            new Set(wallets.map((w) => w.organization?.organizationName).filter(Boolean))
        ) as string[];
    }, [wallets]);

    const filteredWallets = useMemo(() => {
        if (!wallets) return [];
        return wallets.filter((w) => {
            const matchSearch = search
                ? w.asset.symbol.toLowerCase().includes(search.toLowerCase()) ||
                  (w.organization?.organizationName ?? "").toLowerCase().includes(search.toLowerCase())
                : true;
            const matchMoeda = moeda ? w.asset.symbol === moeda : true;
            const matchEmpresa = empresa ? w.organization?.organizationName === empresa : true;
            return matchSearch && matchMoeda && matchEmpresa;
        });
    }, [wallets, search, moeda, empresa]);

    const limparFiltros = () => {
        setSearch("");
        setMoeda("");
        setEmpresa("");
        setStatus("");
    };

    if (!wallets) {
        return (
            <div className="wallets-list-wrapper">
                <div className="wallets-list-page">carregando...</div>
            </div>
        );
    }

    if (wallets.length === 0) {
        return (
            <div className="wallets-list-wrapper">
                <div className="wallets-list-page">
                    <EmptyState
                        icon={WalletIcon}
                        title="Voce ainda nao tem carteiras cadastradas."
                        description="Fale com o seu gerente de conta para liberar a primeira."
                    />
                </div>
            </div>
        );
    }

    const walletsByCurrency = groupWalletsByCurrency(filteredWallets);

    return (
        <div className="wallets-list-wrapper">
            <div className="wallets-list-page">

                <h1 className="title">Lista de carteiras
                    <small>Visualize e gerencie todas as suas carteiras em diferentes moedas.</small>
                </h1>

                <div className="filters-bar">
                    <div className="search-input">
                        <Search size={16} />
                        <input
                            placeholder="Buscar por moeda ou empresa"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <select value={moeda} onChange={(e) => setMoeda(e.target.value)}>
                        <option value="">Moedas</option>
                        {moedasDisponiveis.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>

                    <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
                        <option value="">Empresas</option>
                        {empresasDisponiveis.map((e) => (
                            <option key={e} value={e}>{e}</option>
                        ))}
                    </select>

                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">Status</option>
                    </select>

                    <button className="btn-pill ghost" onClick={limparFiltros}>
                        <XCircle size={14} /> Limpar filtro
                    </button>
                </div>

                {Object.entries(walletsByCurrency).map(([symbol, groupWallets]) => {
                    if (groupWallets.length === 0) return null;

                    const assetName = groupWallets[0].asset.name;
                    const total = sumWalletsBalance(groupWallets);

                    return (
                        <div className="currency-grid">
                            <PageCard key={symbol} className="walletslist-page-card">
                                <div className="currency-section-header">
                                    <div className="currency-label">
                                        <span className="circle">{symbol}</span>
                                        <strong>{assetName}</strong>
                                    </div>
                                    <span className="currency-total">
                                        {formatAssetWithSymbol(total, symbol)}
                                    </span>
                                </div>

                                <div className="wallets-grid">
                                    {groupWallets.map((wallet, i) => (
                                        <WalletCard key={i} wallet={wallet} />
                                    ))}
                                </div>
                            </PageCard>
                        </div>
                    );
                })}

                {filteredWallets.length === 0 && (
                    <EmptyState
                        icon={Search}
                        title="Nenhuma carteira encontrada"
                        description="Tente ajustar os filtros de busca."
                    />
                )}

            </div>
        </div>
    );
};

export default WalletsList;